// Catalog.ID Client — `bitcash-wallets-release` consent modal
// ============================================================================
// WHY THIS MODE HAS A DIALOG WHEN `bitcash-wallets-save` DOES NOT
//
// Save brings ONE wallet the manager already holds INTO the member's list, and
// the member sees it in Settings the moment it lands — a dialog per imported
// wallet would train click-through on the one surface where a member has to
// stay awake. Release goes the other way and is categorically larger: after it,
// the bitcash manager on THIS machine can spend from EVERY wallet the account
// holds, sealed under that machine's own keyring, until the member removes them
// there. That is a decision, and a decision is asked.
//
// WHAT THE DIALOG MUST NAME
//   * the ACCOUNT whose wallets are released — an approval binds to whoever is
//     signed in, and a member with two identities must be able to see which;
//   * the REQUESTER (hub-attested `bitcash`) and the machine's own label, so a
//     prompt on the wrong machine, or from the wrong module, is visible;
//   * the WRAP KEY fingerprint — the key the release is sealed to. It is what
//     the signed request committed to and what the manager must hold to open
//     the release; showing it makes a substituted wrap key something a careful
//     member could notice, on top of the digest binding;
//   * what it MEANS, in money terms: spend capability for every wallet, and a
//     full compromise of this machine yields the same.
//
// WHY IT HAS A POLICY SLOT WHEN THE MAIL EXPORT DOES NOT
// The mail export refuses to be remembered: a standing grant would let a
// compromised mail backend pull the account key onto any machine it runs on,
// unseen. Here the grant is scoped to the machine that already holds the
// wallets — the manager re-asks on every load, and a member who answered "only
// on this machine" once should not be asked at every launch of the wallet app
// they installed on purpose. "Allow always" is per (cid, `bitcash`,
// `bitcash-wallets-release`) in the ordinary policy store, visible and
// revocable in Connected Modules like every other standing grant.

import type { Shell } from '@catalog-shell';
import { createFieldGroup, createFormRow, createElement } from '@catalog-shell';

export type BitcashWalletsReleaseConsentDecision = 'decline' | 'once' | 'session' | 'always';

export interface BitcashWalletsReleaseConsentInput {
    /** The signed-in account whose wallets are released. */
    owningCid: string;
    /** Hub-attested requester (pinned to `bitcash` upstream; rendered, not trusted). */
    requesterId: string;
    /** The requesting machine's label, as the module named it. Display only. */
    deviceLabel?: string;
    /** 128-hex SHA-512 of the ephemeral wrap key the release is sealed to. */
    wrapKeyFingerprint: string;
}

export function showBitcashWalletsReleaseConsent(
    shell: Shell,
    input: BitcashWalletsReleaseConsentInput,
): Promise<BitcashWalletsReleaseConsentDecision> {
    return new Promise<BitcashWalletsReleaseConsentDecision>((resolve) => {
        let resolved = false;
        const resolveOnce = (d: BitcashWalletsReleaseConsentDecision): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(d);
        };

        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });
        // `textContent` (never innerHTML) throughout: the label arrives from
        // another process and is display text, never markup.
        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: 'Let the wallet manager on this machine spend from your wallets?',
        }));

        const accountGroup = createFieldGroup({ title: 'Releasing from' });
        const accountRow = createFormRow({ label: 'Catalog.ID account' });
        accountRow.content.appendChild(createElement('code', {
            className: 'cs-field-view-text',
            textContent: input.owningCid,
        }));
        accountGroup.body.appendChild(accountRow.element);
        const requesterRow = createFormRow({ label: 'Requested by' });
        requesterRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: input.deviceLabel
                ? `${input.requesterId} on "${input.deviceLabel}"`
                : input.requesterId,
        }));
        accountGroup.body.appendChild(requesterRow.element);
        container.appendChild(accountGroup.element);

        container.appendChild(createElement('div', {
            className: 'cs-inline-warn',
            textContent:
                'The wallet manager on this machine will keep a sealed copy of every wallet key this '
                + 'account holds, so it can spend from all of them without asking your Catalog.ID '
                + 'again — until you remove them there. Anyone who fully compromises this machine '
                + 'could spend that money. The keys are sent wrapped to a one-time key only this '
                + 'machine holds, and your Catalog.ID keeps the master list.',
        }));

        const keyGroup = createFieldGroup({ title: 'What is released' });
        const whatRow = createFormRow({ label: 'Wallets' });
        whatRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: 'Every wallet on this account — retired ones included, so the manager '
                + 'knows to stop using them',
        }));
        keyGroup.body.appendChild(whatRow.element);
        const wrapRow = createFormRow({ label: 'Sealed to' });
        wrapRow.content.appendChild(createElement('code', {
            textContent: shortFp(input.wrapKeyFingerprint),
            attributes: { title: input.wrapKeyFingerprint },
        }));
        keyGroup.body.appendChild(wrapRow.element);
        container.appendChild(keyGroup.element);

        const actions = createElement('div', { className: 'cs-form-actions' });
        const btn = (
            label: string, className: string, decision: BitcashWalletsReleaseConsentDecision,
        ): HTMLButtonElement => {
            const element = createElement('button', { className, textContent: label }) as HTMLButtonElement;
            element.addEventListener('click', () => resolveOnce(decision));
            return element;
        };
        actions.appendChild(btn('Decline', 'cs-btn cs-btn--no', 'decline'));
        actions.appendChild(btn('Just this once', 'cs-btn cs-btn--default', 'once'));
        actions.appendChild(btn('For this session', 'cs-btn cs-btn--default', 'session'));
        actions.appendChild(btn('Always on this machine', 'cs-btn cs-btn--yes', 'always'));
        container.appendChild(actions);

        const handle = shell.showModal(container, {
            size: 'wide',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            // A dismissed dialog is a refusal. Never a silent approval, and
            // never a hang: the bitcash module is waiting on this promise.
            onClose: () => resolveOnce('decline'),
        });
    });
}

function shortFp(fp: string): string {
    return fp.length <= 24 ? fp : `${fp.slice(0, 8)}…${fp.slice(-8)}`;
}
